import { useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { AmplifyUser } from "@aws-amplify/ui";
import { QueryStatus } from "../../app/types";
import { CognitoUserGroups } from "../Users/userUtils";
import { useAppDispatch } from "../../app/hooks";
import { setCurrentUser, setUserCognitoGroups } from "../Users/usersSlice";
import { User } from "../../API";
import { Mixpanel } from "../Analytics/Mixpanel";
import { fetchUsersByEmail } from "../Users/usersAPI";
import { useAllPrograms } from "../Programs/programUtils";
import { useAllNetworks } from "../Networks/networkUtils";
import { useAllInstitutions } from "../Institutions/institutionUtils";
import { useFirebaseFeatureFlags } from "../Firebase/firebaseUtils";
import { refreshMessageQueue } from "../MessageQueue/messageQueueSlice";

interface useAuthProps {
  amplifyUser?: AmplifyUser;
}

export const useAuth = ({ amplifyUser }: useAuthProps) => {
  const [ddbUser, setDdbUser] = useState<User | null>(null);
  const [status, setStatus] = useState<QueryStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const { getAllPrograms } = useAllPrograms();
  const { getAllNetworks } = useAllNetworks();
  const { getAllInstitutions } = useAllInstitutions();
  const { getFeatureFlags } = useFirebaseFeatureFlags();

  const handleUserChange = async (amplifyUser: AmplifyUser) => {
    // No-op debug helper (disabled)
    const debugAppend = (_label: string, _data?: unknown) => {};
    setStatus("loading");
    setError(null);
    try {
      // debugAppend available in this scope

      // Get permissions (robust to shape differences)
      let userGroups: CognitoUserGroups[] = [];
      try {
        const session = await Auth.currentSession();
        userGroups = (session.getAccessToken().payload["cognito:groups"] ||
          []) as CognitoUserGroups[];
      } catch {
        try {
          // Fallback to object method if available
          userGroups =
            (amplifyUser as any)?.getSignInUserSession?.()?.getAccessToken?.()
              ?.payload?.["cognito:groups"] || [];
        } catch {}
      }
      dispatch(setUserCognitoGroups(userGroups));
      debugAppend("cognito_groups", userGroups);

      // Check if user is SysAdmin (needs institutions in auth flow)
      const isSysAdmin = userGroups.includes(CognitoUserGroups.SysAdmins);

      // Extract email with fallbacks
      let amplifyUserEmail: string | undefined =
        amplifyUser.attributes?.email?.toLowerCase();
      if (!amplifyUserEmail) {
        try {
          const current = await Auth.currentAuthenticatedUser();
          amplifyUserEmail = current?.attributes?.email?.toLowerCase();
        } catch {}
      }
      debugAppend("email", amplifyUserEmail || null);
      // (remove noisy page host log)
      if (amplifyUserEmail) {
        Mixpanel.identify(amplifyUserEmail);
        // Fetch the associated ddbUsers
        debugAppend("fetchUsersByEmail_start", amplifyUserEmail);
        let usersWithEmail: User[] | null = null;
        try {
          usersWithEmail = await fetchUsersByEmail(amplifyUserEmail);
        } catch (e: any) {
          debugAppend("fetchUsersByEmail_error", {
            message: e?.message,
            stack: e?.stack,
            err: e
          });
          throw e;
        }
        debugAppend(
          "fetchUsersByEmail_result_count",
          usersWithEmail ? usersWithEmail.length : 0
        );
        if (usersWithEmail && usersWithEmail.length > 0) {
          // Grab the first one in the list (should only ever have 1, but we only have a query that returns a list atm.)
          const firstUserInList = usersWithEmail[0];
          setDdbUser(firstUserInList);
          dispatch(setCurrentUser(firstUserInList));
          debugAppend("ddbUser_set", {
            id: firstUserInList.id,
            email: firstUserInList.email
          });

          // Load programs, networks, and institutions (for SysAdmins) in parallel
          try {
            debugAppend("getAllPrograms_start");
            debugAppend("getAllNetworks_start");
            if (isSysAdmin) {
              debugAppend("getAllInstitutions_start");
            }

            const loadPromises = [
              getAllPrograms(),
              getAllNetworks(),
              ...(isSysAdmin ? [getAllInstitutions()] : [])
            ];

            await Promise.all(loadPromises);

            debugAppend("getAllPrograms_done");
            debugAppend("getAllNetworks_done");
            if (isSysAdmin) {
              debugAppend("getAllInstitutions_done");
            }
          } catch (e: any) {
            debugAppend("getAllData_error", {
              message: e?.message,
              stack: e?.stack,
              err: e
            });
            throw e;
          }

          try {
            debugAppend("getFeatureFlags_start");
            await getFeatureFlags();
            debugAppend("getFeatureFlags_done");
          } catch (e: any) {
            debugAppend("getFeatureFlags_error", {
              message: e?.message,
              stack: e?.stack,
              err: e
            });
            throw e;
          }

          dispatch(refreshMessageQueue());
        } else {
          console.error("this email has no associated user in Dynamo.");
        }
      } else {
        setStatus("failed");
        const msg = "No email found on Cognito user after login";
        setError(msg);
        console.error(msg);
        return;
      }
      setStatus("succeeded");
    } catch (error) {
      debugAppend("useAuth_outer_catch", {
        message: (error as any)?.message,
        stack: (error as any)?.stack,
        err: error
      });
      setStatus("failed");
      const errorString = `Error in useAuth: ${JSON.stringify(error)}`;
      setError(errorString);
      console.error(errorString);
    }
  };

  useEffect(() => {
    if (amplifyUser) {
      handleUserChange(amplifyUser);
    }
  }, [amplifyUser]);

  return {
    ddbUser,
    status,
    error
  };
};

export const AuthNotifications = {
  NoDDBUser:
    "Your account does not have a corresponding User. Please try refreshing your browser or contact your administrator for further assistance.",
  FetchUserError:
    "There was an error fetching this user. Please try again or contact your administrator."
};
